import ArrowIcon from '@mui/icons-material/ChevronRight'
import { Box, Card, CardActions, CardContent, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import { useTranslate } from 'ra-core'
import {
  ReferenceField,
  useGetRecordRepresentation,
  usePermissions,
  useRecordContext,
  useResourceContext,
} from 'react-admin'

import { CardTitle } from '.'
import { getResourceByName } from '..'
import ActionButton from '../../components/ActionButton'
import ReferenceLink from '../../components/ReferenceLink'
import { SYSTEM_PERMISSION_ACTIONS, SYSTEM_PERMISSION_READ } from '../../config/permissions'
import { useCommonStyles } from '../../config/theme'
import { hasResourcePermission, useResourcePermissions } from '../../domain/permissions'
import { isAllowed } from '../../utils'

export const useShowStyles = () => ({
  row: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  unorderedList: {
    marginTop: 4,
    marginBottom: 0,
    paddingLeft: 18,
  },
  unorderedListItem: {
    marginBottom: 8,
    '&:lastChild': {
      marginBottom: 0,
    },
    '& > p': {
      fontSize: 14,
      marginTop: 0,
      marginBottom: 4,
    },
  },
  actionsContainer: (theme) => ({
    borderTop: `solid 1px ${theme.palette.divider}`,
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    flexWrap: 'wrap',
    '& > *': {
      marginLeft: theme.spacing(0.5) + ' !important',
      marginRight: theme.spacing(0.5) + ' !important',
      marginBottom: theme.spacing(1) + ' !important',
      '&:lastChild': {
        marginRight: '0 !important',
      },
    },
  }),
  fieldContainer: (theme) => ({
    display: 'flex',
    flexWrap: 'wrap',
    borderTop: `solid 1px ${theme.palette.divider}`,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0 + ' !important',
    paddingBottom: theme.spacing(2) + ' !important',
    '&:lastChild': {
      paddingBottom: theme.spacing(3) + ' !important',
    },
    '& > .ra-field': {
      margin: theme.spacing(2),
      marginBottom: 0,
    },
  }),
  subTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 26,
    boxSizing: 'content-box',
  },
  map: {
    padding: '0 !important',
    maxWidth: 480,
  },
  textFieldWithFullWidth: {
    width: '100%',
    margin: '0 !important',
  },
})

export const Title = () => {
  const commonClasses = useCommonStyles()
  const resource = useResourceContext()
  const record = useRecordContext()
  const recordRepresentation = useGetRecordRepresentation(resource)
  const translate = useTranslate()
  return (
    <Box sx={commonClasses.textWithLargeIcons}>
      {translate(`resources.${resource}.name`, 2)}
      <ArrowIcon titleAccess=" > " />
      {recordRepresentation(record)}
    </Box>
  )
}

export const ShowCard = ({ children }) => {
  return <Card sx={{ mt: 3 }}>{children}</Card>
}

export const ShowReference = ({ children, reference, source, record }) => (
  <ReferenceField reference={reference} source={source} link={false} record={record}>
    <ShowCard>{children}</ShowCard>
  </ReferenceField>
)

export const ShowActions = ({ allowedActions, className, helperText }) => {
  const resource = useResourceContext()
  const [hasActions] = useResourcePermissions(resource, SYSTEM_PERMISSION_ACTIONS)
  const classes = useShowStyles()

  if (!hasActions || isEmpty(allowedActions)) {
    return null
  }
  return (
    <>
      <CardActions sx={classes.actionsContainer} className={className}>
        {allowedActions.map((action) => (
          <ActionButton key={action} action={action} />
        ))}
      </CardActions>
      {helperText && (
        <Typography variant="caption" sx={{ color: 'text.secondary', p: 2, pt: 0, display: 'block' }}>
          {helperText}
        </Typography>
      )}
    </>
  )
}

export const ShowReferenceLinks = ({ excluded = [], filterValues, record, sources = {} }) => {
  const { permissions } = usePermissions()
  const resource = useResourceContext()
  const classes = useShowStyles()

  const resourceConfig = getResourceByName(resource)
  let { references } = resourceConfig.options
  references = (references || []).filter(
    (r) => hasResourcePermission(permissions, r, SYSTEM_PERMISSION_READ) && isAllowed(excluded, r),
  )
  return references.length > 0 ? (
    <CardActions sx={classes.actionsContainer}>
      {references
        .map((reference) => {
          const referenceConfig = getResourceByName(reference)
          const referenceKey = referenceConfig?.options.referenceKey
          return referenceConfig ? (
            <ReferenceLink
              key={referenceConfig.name}
              record={record}
              reference={referenceConfig.name}
              source={sources[referenceKey] ?? referenceKey}
              filterValues={filterValues}
              variant="outlined"
            />
          ) : null
        })
        .filter(Boolean)}
    </CardActions>
  ) : null
}

export const GenericCardTitle = ({ text, sx }) => {
  const resource = useResourceContext()
  return <CardTitle text={`resources.${resource}.show.titles.${text}`} size={17} sx={sx} />
}

export const ShowSubSectionTitleCard = ({ text }) => {
  const showClasses = useShowStyles()
  const commonClasses = useCommonStyles()
  return (
    <CardContent sx={{ ...showClasses.subTitleContainer, ...commonClasses.borderTop }}>
      <GenericCardTitle text={text} />
    </CardContent>
  )
}
